/**
 * MCP Tool Schema Registry
 *
 * Fetches JSON Schema input models at runtime from Robot's
 * GET /api/llm/extension/tools/schemas endpoint.
 *
 * Robot generates schemas directly from Pydantic models,
 * so any new tool added on Robot is automatically available.
 */

import { fetchRobot } from '../utils/robot';

export interface ToolInputSchema {
  type: 'object';
  properties: Record<string, unknown>;
  required?: string[];
  title?: string;
  [key: string]: unknown;
}

interface RobotToolSchema {
  functionName: string;
  inputSchema: ToolInputSchema;
}

type RobotToolSchemasResponse = Record<string, RobotToolSchema>;

export class ToolSchemaRegistry {

  private static cache: RobotToolSchemasResponse | null = null;

  /* Use Cache*/
  static async fetchSchemas(): Promise<RobotToolSchemasResponse> {
    if (this.cache) {
      return this.cache;
    }
    try {
      const response = await fetchRobot('api/llm/extension/tools/schemas', 'GET');
      this.cache = response as RobotToolSchemasResponse;
      return this.cache;
    } catch (error) {
      console.error('[MCP] Failed to fetch tool schemas from Robot:', error);
      return {};
    }
  }

  static async get(functionName: string): Promise<ToolInputSchema | undefined> {
    const schemas = await this.fetchSchemas();
    return schemas[functionName]?.inputSchema;
  }

  static async has(functionName: string): Promise<boolean> {
    const schemas = await this.fetchSchemas();
    return functionName in schemas;
  }

  static async keys(): Promise<string[]> {
    const schemas = await this.fetchSchemas();
    return Object.keys(schemas);
  }

  static invalidate(): void {
    this.cache = null;
  }
}
